import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { EnvelopeSimple, Clock, Bell, PaperPlaneRight, Eye, Info, CheckCircle } from "@phosphor-icons/react";

export default function Settings() {
  const [s, setS] = useState(null);
  const [recipients, setRecipients] = useState("");
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [senderEmail, setSenderEmail] = useState("");
  const [testTo, setTestTo] = useState("");
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const load = async () => {
    const { data } = await api.get("/settings");
    setS(data);
    setRecipients((data.report_recipients || []).join(", "));
    setSenderEmail(data.sender_email || "");
  };
  useEffect(() => { load(); }, []);

  const save = async (patch) => {
    try {
      const { data } = await api.put("/settings", patch);
      setS(data);
      toast.success("Ayarlar kaydedildi");
    } catch (e) { toast.error(e?.response?.data?.detail || "Kaydedilemedi"); }
  };

  const saveRecipients = async () => {
    const list = recipients.split(/[\n,;]/).map((x) => x.trim()).filter(Boolean);
    await save({ report_recipients: list });
  };

  const preview = async () => {
    try {
      const { data } = await api.post("/settings/report/preview");
      setPreviewHtml(data.html);
      setPreviewOpen(true);
    } catch (e) { toast.error("Önizleme alınamadı"); }
  };

  const sendNow = async () => {
    try {
      const { data } = await api.post("/settings/report/send-now");
      if (data.status === "sent") toast.success(`Rapor gönderildi (${(data.email?.recipients || []).length} kişi)`);
      else if (data.status === "mocked") toast.info("MOCK modda — email göndermek için RESEND_API_KEY tanımlı olmalı");
      else if (data.status === "no_recipients") toast.error("Alıcı listesi boş");
      else if (data.status === "disabled") toast.error("Email raporlaması kapalı");
      else toast.info(`Sonuç: ${data.status}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gönderilemedi"); }
  };

  const sendTestEmail = async () => {
    if (!testTo) return toast.error("Alıcı e-posta gerekli");
    setTesting(true); setTestResult(null);
    try {
      const { data } = await api.post("/settings/test-email", { to: testTo });
      setTestResult(data);
      if (data.status === "sent") toast.success(`Test e-postası gönderildi (from: ${data.from})`);
      else if (data.status === "mocked") toast.info("Mock modda — RESEND_API_KEY yok");
      else if (data.status === "error") toast.error(`Resend hatası — detay altta`);
      else toast.info(`Sonuç: ${data.status}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gönderilemedi"); }
    finally { setTesting(false); }
  };

  const saveSenderEmail = async () => {
    await save({ sender_email: senderEmail.trim() || null });
    toast.success("Gönderen adresi güncellendi");
  };

  if (!s) return <div className="p-8"><div className="pulse-dot" /></div>;

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Sistem</div>
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight">Ayarlar</h1>
      </div>

      {/* Resend / E-posta Gönderim Ayarları */}
      <Card className="rounded-sm border-border" data-testid="resend-settings-card">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center gap-2">
            <EnvelopeSimple size={22} weight="fill" className="text-primary" />
            <h3 className="font-semibold uppercase tracking-widest text-sm">
              Resend / E-posta Gönderim Ayarları
            </h3>
          </div>

          <div className="p-4 rounded-sm bg-blue-50 border border-blue-200 space-y-2">
            <div className="flex items-start gap-2">
              <Info size={16} className="text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="text-xs text-blue-900 leading-relaxed">
                <div className="font-semibold mb-1">Resend Sandbox → Prod Geçişi</div>
                <p>Şu an sistemi ilk kurduğunuzda Resend&apos;in test domain&apos;i (<code>onboarding@resend.dev</code>) kullanılıyor.
                Bu sandbox modu <b>sadece Resend hesabınızın sahibi olan e-posta adresine</b> gönderim yapabilir.
                Müşterilere ve kurumsal alıcılara mail atmak için:</p>
                <ol className="list-decimal ml-5 mt-2 space-y-1">
                  <li>
                    <a href="https://resend.com/domains" target="_blank" rel="noreferrer"
                       className="text-blue-700 underline font-semibold">resend.com/domains</a>
                    &apos;a git → <b>Add Domain</b>&apos;a bas → kendi domain&apos;ini gir (örn. <code>otoparkim.com</code>)
                  </li>
                  <li>Resend size <b>SPF, DKIM ve DMARC</b> DNS kayıtları verecek</li>
                  <li>Domain sağlayıcınızda (Cloudflare, GoDaddy, Namecheap...) bu kayıtları ekleyin</li>
                  <li>Resend panelinde <b>Verify</b>&apos;e basın (~5–60 dk sürer)</li>
                  <li>Doğrulama tamamlanınca aşağıya <code>noreply@sizindomain.com</code> gibi bir adres yazın ve <b>Kaydet</b>&apos;e basın</li>
                  <li>Sağdaki <b>Test Mail Gönder</b> ile kendinize deneme atın</li>
                </ol>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Gönderen E-posta (doğrulanmış &quot;from&quot; adresi)</Label>
              <div className="flex gap-2">
                <Input
                  value={senderEmail}
                  onChange={(e) => setSenderEmail(e.target.value)}
                  placeholder="noreply@sizindomain.com (boş = onboarding@resend.dev)"
                  className="rounded-sm font-mono"
                  data-testid="setting-sender-email"
                />
                <Button
                  onClick={saveSenderEmail}
                  data-testid="save-sender-btn"
                  className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-widest font-semibold whitespace-nowrap"
                >
                  Kaydet
                </Button>
              </div>
              <div className="text-[11px] text-muted-foreground mt-1">
                Boş bırakırsanız Resend sandbox adresi kullanılır. Kendi domain&apos;iniz
                doğrulanmadan buraya farklı bir adres yazmayın.
              </div>
            </div>
            <div>
              <Label>Test E-postası Alıcısı</Label>
              <div className="flex gap-2">
                <Input
                  type="email"
                  value={testTo}
                  onChange={(e) => setTestTo(e.target.value)}
                  placeholder="kendi@email.com"
                  className="rounded-sm font-mono"
                  data-testid="test-email-to"
                />
                <Button
                  variant="outline"
                  onClick={sendTestEmail}
                  disabled={testing}
                  data-testid="test-email-btn"
                  className="rounded-sm uppercase tracking-widest font-semibold whitespace-nowrap"
                >
                  <PaperPlaneRight size={14} /> {testing ? "Gönderiliyor..." : "Test Mail"}
                </Button>
              </div>
              <div className="text-[11px] text-muted-foreground mt-1">
                Sandbox modundaysa sadece hesap sahibinin adresi çalışır.
              </div>
            </div>
          </div>

          {testResult && (
            <div className={`p-3 rounded-sm border-2 ${
              testResult.status === "sent"
                ? "border-emerald-400 bg-emerald-50"
                : testResult.status === "mocked"
                  ? "border-amber-400 bg-amber-50"
                  : "border-red-400 bg-red-50"
            }`}>
              <div className="flex items-center gap-2 mb-1">
                {testResult.status === "sent"
                  ? <CheckCircle size={16} weight="fill" className="text-emerald-600" />
                  : <Info size={16} className="text-red-600" />}
                <span className="text-xs uppercase tracking-widest font-semibold">
                  {testResult.status === "sent" ? "Gönderildi" : testResult.status.toUpperCase()}
                </span>
              </div>
              <div className="text-xs font-mono break-all">
                {testResult.from && <div>from: <b>{testResult.from}</b></div>}
                {testResult.recipients && <div>to: {testResult.recipients.join(", ")}</div>}
                {testResult.id && <div>id: {testResult.id}</div>}
                {testResult.error && <div className="text-red-700 mt-1">{testResult.error}</div>}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="rounded-sm border-border">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center gap-2">
            <EnvelopeSimple size={22} weight="fill" className="text-primary" />
            <h3 className="font-semibold uppercase tracking-widest text-sm">Günlük Email Raporu</h3>
          </div>

          <div className="flex items-center justify-between py-2 border-b border-border">
            <div>
              <div className="font-medium">Email raporu aktif</div>
              <div className="text-xs text-muted-foreground">Her gün ayarlı saatte otomatik gönderir</div>
            </div>
            <Switch checked={!!s.email_enabled} onCheckedChange={(v) => save({ email_enabled: v })} data-testid="setting-email-enabled" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Gönderim Saati (HH:MM)</Label>
              <Input
                defaultValue={s.report_time}
                onBlur={(e) => save({ report_time: e.target.value })}
                className="rounded-sm font-mono"
                data-testid="setting-report-time"
              />
              <div className="text-xs text-muted-foreground mt-1">Europe/Istanbul saat dilimi</div>
            </div>
            <div>
              <Label>Uzun Süre Uyarı Eşiği (saat)</Label>
              <Input
                type="number"
                defaultValue={s.long_stay_alert_hours}
                onBlur={(e) => save({ long_stay_alert_hours: Number(e.target.value) })}
                className="rounded-sm font-mono"
              />
            </div>
          </div>

          <div>
            <Label>Alıcı Email Listesi (virgül veya yeni satırla ayır)</Label>
            <Textarea
              value={recipients}
              onChange={(e) => setRecipients(e.target.value)}
              rows={3}
              placeholder="mudur@sirket.com, muhasebe@sirket.com"
              className="rounded-sm"
              data-testid="setting-recipients"
            />
            <div className="flex gap-2 mt-2">
              <Button onClick={saveRecipients} className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-widest font-semibold" data-testid="save-recipients-btn">
                Alıcıları Kaydet
              </Button>
              <Button variant="outline" onClick={preview} className="rounded-sm uppercase tracking-widest font-semibold" data-testid="preview-report-btn">
                <Eye size={16} /> Önizle
              </Button>
              <Button variant="outline" onClick={sendNow} className="rounded-sm uppercase tracking-widest font-semibold" data-testid="send-now-btn">
                <PaperPlaneRight size={16} /> Şimdi Gönder
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-sm border-border">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center gap-2">
            <Bell size={22} className="text-primary" />
            <h3 className="font-semibold uppercase tracking-widest text-sm">Bildirimler</h3>
          </div>
          <div className="flex items-center justify-between py-2 border-b border-border">
            <div>
              <div className="font-medium">SMS bildirimi</div>
              <div className="text-xs text-muted-foreground">Yakında — altyapı hazır, entegrasyon henüz aktif değil</div>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="rounded-sm text-[10px] uppercase tracking-widest">Pasif</Badge>
              <Switch disabled checked={!!s.sms_enabled} />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-sm border-border">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center gap-2">
            <Clock size={22} className="text-primary" />
            <h3 className="font-semibold uppercase tracking-widest text-sm">ANPR & Webhook</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Duplicate Okuma Penceresi (sn)</Label>
              <Input
                type="number"
                defaultValue={s.duplicate_read_window_seconds}
                onBlur={(e) => save({ duplicate_read_window_seconds: Number(e.target.value) })}
                className="rounded-sm font-mono"
                data-testid="setting-duplicate-read-window"
              />
              <div className="text-xs text-muted-foreground mt-1">Aynı plaka bu süre içinde tekrar okunursa yok sayılır</div>
            </div>
            <div>
              <Label>Minimum Güven Eşiği (0-1)</Label>
              <Input
                type="number"
                step="0.05"
                min="0"
                max="1"
                defaultValue={s.confidence_threshold}
                onBlur={(e) => save({ confidence_threshold: Number(e.target.value) })}
                className="rounded-sm font-mono"
                data-testid="setting-confidence-threshold"
              />
              <div className="text-xs text-muted-foreground mt-1">Altındaki okumalar reddedilir</div>
            </div>
            <div className="md:col-span-2">
              <Label>OCR Titreme Penceresi (sn)</Label>
              <Input
                type="number"
                min="0"
                defaultValue={s.plate_flicker_window_seconds ?? 120}
                onBlur={(e) => save({ plate_flicker_window_seconds: Number(e.target.value) })}
                className="rounded-sm font-mono"
                data-testid="setting-plate-flicker-window"
              />
              <div className="text-xs text-muted-foreground mt-1">
                OCR titreme koruması: bu süre içinde girmiş, plakası 1 karakter farklı araç varsa yeni giriş engellenir.
                <span className="text-amber-700 font-semibold"> Filo araçları (28ABC001, 28ABC002 …) sorun yaşarsa düşürün veya 0 yapın.</span>
                {" "}Varsayılan: 120 sn. 0 = kapalı.
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-sm border-border">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center gap-2">
            <Clock size={22} className="text-primary" />
            <h3 className="font-semibold uppercase tracking-widest text-sm">Kapasite & Doluluk</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Doluluk Uyarı Eşiği (%)</Label>
              <Input
                type="number"
                min="50" max="100"
                defaultValue={s.capacity_alert_percent}
                onBlur={(e) => save({ capacity_alert_percent: Number(e.target.value) })}
                className="rounded-sm font-mono"
                data-testid="setting-capacity-alert"
              />
              <div className="text-xs text-muted-foreground mt-1">Bu doluluk oranından itibaren operatöre push bildirimi gönderilir</div>
            </div>
            <div className="flex items-center justify-between py-2">
              <div>
                <div className="font-medium">Otopark dolduğunda girişi engelle</div>
                <div className="text-xs text-muted-foreground">Kapasite %100&apos;e ulaştığında yeni araç girişi reddedilir</div>
              </div>
              <Switch
                checked={!!s.block_when_full}
                onCheckedChange={(v) => save({ block_when_full: v })}
                data-testid="setting-block-when-full"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Rapor Önizleme</DialogTitle></DialogHeader>
          <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
